import { useState, useEffect } from 'react'
import { fetchWeatherApi } from 'openmeteo'
import { Sun, Moon, Cloud, CloudRain, Wind, CloudDrizzle, CloudSnow } from 'lucide-react'

interface WeatherData {
  temperature: number
  isDay: boolean
  weatherCode: number
}

// Cache for weather data to avoid refetching
let weatherCacheData: { data: WeatherData, timestamp: number } | null = null
const WEATHER_CACHE_DURATION = 10 * 60 * 1000 // 10 minutes (weather changes more frequently)

const WEATHER_CODES = {
  // Clear
  0: { icon: Sun, condition: 'clear' },
  1: { icon: Sun, condition: 'clear' },
  // Partly cloudy
  2: { icon: Cloud, condition: 'cloudy' },
  3: { icon: Cloud, condition: 'cloudy' },
  // Fog
  45: { icon: Cloud, condition: 'fog' },
  48: { icon: Cloud, condition: 'fog' },
  // Drizzle
  51: { icon: CloudDrizzle, condition: 'drizzle' },
  53: { icon: CloudDrizzle, condition: 'drizzle' },
  55: { icon: CloudDrizzle, condition: 'drizzle' },
  // Rain
  56: { icon: CloudRain, condition: 'rain' },
  57: { icon: CloudRain, condition: 'rain' },
  61: { icon: CloudRain, condition: 'rain' },
  63: { icon: CloudRain, condition: 'rain' },
  65: { icon: CloudRain, condition: 'rain' },
  // Snow
  71: { icon: CloudSnow, condition: 'snow' },
  73: { icon: CloudSnow, condition: 'snow' },
  75: { icon: CloudSnow, condition: 'snow' },
  // Thunderstorm
  95: { icon: CloudRain, condition: 'rain' },
  96: { icon: CloudRain, condition: 'rain' },
  99: { icon: CloudRain, condition: 'rain' },
}

export function WeatherWidget() {
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchWeather()
    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchWeather = async () => {
    // Check cache first
    if (weatherCacheData && Date.now() - weatherCacheData.timestamp < WEATHER_CACHE_DURATION) {
      setWeather(weatherCacheData.data)
      setLoading(false)
      return
    }

    try {
      const params = {
        latitude: 28.6139,
        longitude: 77.2090,
        current: ["temperature_2m", "is_day", "weather_code"],
        timezone: "Asia/Kolkata",
      }
      
      const url = "https://api.open-meteo.com/v1/forecast"
      const responses = await fetchWeatherApi(url, params)
      const response = responses[0]

      const current = response.current()!
      const temperature = current.variables(0)!.value()
      const isDay = current.variables(1)!.value()
      const weatherCode = current.variables(2)!.value()

      const weatherData = {
        temperature: Math.round(temperature),
        isDay: isDay === 1,
        weatherCode: weatherCode,
      }

      // Cache the result
      weatherCacheData = {
        data: weatherData,
        timestamp: Date.now()
      }

      setWeather(weatherData)
    } catch {
      // Set a fallback or error state
      const fallbackData = {
        temperature: 25,
        isDay: true,
        weatherCode: 0,
      }
      
      // Cache fallback data as well to avoid repeated failed calls
      weatherCacheData = {
        data: fallbackData,
        timestamp: Date.now()
      }
      
      setWeather(fallbackData)
    } finally {
      setLoading(false)
    }
  }

  if (loading || !weather) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 backdrop-blur-sm">
        <div className="w-5 h-5 animate-pulse bg-white/20 rounded-full" />
        <div className="w-12 h-4 bg-white/20 rounded animate-pulse" />
      </div>
    )
  }

  const { icon: Icon, condition } = WEATHER_CODES[weather.weatherCode as keyof typeof WEATHER_CODES] || { icon: Sun, condition: 'clear' }

  return (
    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all">
      {condition === 'rain' || condition === 'drizzle' ? (
        <div className="relative">
          <Icon className="w-5 h-5 text-blue-600 animate-bounce" />
          <CloudDrizzle className="w-4 h-4 text-blue-700 absolute -bottom-1 -right-1 animate-pulse" />
        </div>
      ) : condition === 'snow' ? (
        <div className="relative">
          <CloudSnow className="w-5 h-5 text-gray-800 animate-pulse" />
        </div>
      ) : condition === 'cloudy' ? (
        <Icon className="w-5 h-5 text-gray-700" />
      ) : weather.isDay ? (
        <Icon className="w-5 h-5 text-yellow-600 animate-pulse" />
      ) : (
        <Moon className="w-5 h-5 text-gray-800" />
      )}
      <span className="font-semibold text-gray-900">
        {weather.temperature}°C
      </span>
    </div>
  )
}
